/**
 * Shared utility functions for health status display.
 * Provides consistent status colors and labels across the UI.
 */

import type { HealthStatus, ServiceHealthStatusType, BotHealthStatusType } from './api/types.js';

export type AnyHealthStatus = HealthStatus | ServiceHealthStatusType | BotHealthStatusType;

/**
 * Get the Tailwind background color class for a health status.
 * Works with all health status variants including unknown and unreachable.
 */
export function getStatusColor(status?: AnyHealthStatus): string {
  switch (status) {
    case 'healthy':
      return 'bg-green-500';
    case 'degraded':
      return 'bg-yellow-500';
    case 'unhealthy':
      return 'bg-red-500';
    case 'unreachable':
    case 'unknown':
    default:
      return 'bg-gray-500';
  }
}

/**
 * Get the human-readable text label for a health status.
 */
export function getStatusText(status?: AnyHealthStatus): string {
  switch (status) {
    case 'healthy':
      return 'Operational';
    case 'degraded':
      return 'Degraded';
    case 'unhealthy':
      return 'Down';
    case 'unreachable':
      return 'Unreachable';
    case 'unknown':
    default:
      return 'Unknown';
  }
}

/**
 * Get the Tailwind text color class for a health status.
 */
export function getStatusTextClass(status?: AnyHealthStatus): string {
  switch (status) {
    case 'healthy':
      return 'text-green-400';
    case 'degraded':
      return 'text-yellow-400';
    case 'unhealthy':
      return 'text-red-400';
    case 'unreachable':
    case 'unknown':
    default:
      return 'text-gray-400';
  }
}

/**
 * Get the Tailwind badge classes for a health status.
 */
export function getStatusBadgeClass(status?: AnyHealthStatus): string {
  switch (status) {
    case 'healthy':
      return 'bg-green-100 text-green-800';
    case 'degraded':
      return 'bg-yellow-100 text-yellow-800';
    case 'unhealthy':
      return 'bg-red-100 text-red-800';
    case 'unreachable':
    case 'unknown':
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

/**
 * Get both the color and text for a status in one call.
 * Useful when you need both values.
 */
export function getStatusInfo(status?: AnyHealthStatus): {
  color: string;
  text: string;
} {
  return {
    color: getStatusColor(status),
    text: getStatusText(status),
  };
}
